/**
 * The motion vocabulary of the graph.
 *
 * ## The one move that matters
 *
 * A branch card *becomes* the current node. Not "fades out while a new node fades in" —
 * the same object travels. That is a shared-layout animation, and the whole reason
 * framer-motion is a dependency: `layoutId` gets it right across two unrelated subtrees
 * with interruptible springs, and the CSS equivalent is a measurement-and-FLIP engine
 * nobody should be maintaining by hand.
 *
 * `branchLayoutId` is the trick that makes it work. A card for suggestion `S` and the
 * current node reached *by playing* `S` share an id, because a `SessionNode` carries
 * `fromSuggestionId`. So the reducer appending a node is, by itself, enough to make the
 * card fly into place — the UI is told nothing extra.
 *
 * ## Timing
 *
 * Nothing here is awaited by anything. The reducer has already moved on; these springs
 * describe how the pixels catch up, and if a second chord lands 300 ms into the first
 * transition the springs are simply re-targeted mid-flight. That is why they are springs
 * with velocity rather than tweens with durations.
 */

import type { Transition } from 'framer-motion';

/** The budget for a full selection transition, from the brief. */
export const SELECTION_BUDGET_MS = 400;

/**
 * Stiff enough to settle inside the budget, damped enough not to wobble.
 * Roughly 320 ms to visual rest, leaving headroom under `SELECTION_BUDGET_MS`.
 */
export const NODE_SPRING: Transition = {
  type: 'spring',
  stiffness: 420,
  damping: 38,
  mass: 0.9,
};

/** Cards arriving and leaving. Slightly softer than the node move; they are secondary. */
export const CARD_SPRING: Transition = {
  type: 'spring',
  stiffness: 320,
  damping: 34,
  mass: 0.8,
};

/**
 * The reduced-motion substitute: a crossfade, short enough to read as a state change
 * and too short to be a journey. Used for *every* animated property, not just position.
 */
export const CALM_FADE: Transition = { duration: 0.14, ease: 'linear' };

/** Per-card entry delay as a set streams in. Zero under reduced motion. */
export const CARD_STAGGER_S = 0.045;

/** Cap the stagger so a six-card set does not take a third of a second to appear. */
export const MAX_STAGGER_INDEX = 5;

export function cardDelay(index: number, calm: boolean): number {
  if (calm) return 0;
  return Math.min(index, MAX_STAGGER_INDEX) * CARD_STAGGER_S;
}

export function nodeTransition(calm: boolean): Transition {
  return calm ? CALM_FADE : NODE_SPRING;
}

export function cardTransition(index: number, calm: boolean): Transition {
  return calm ? CALM_FADE : { ...CARD_SPRING, delay: cardDelay(index, calm) };
}

/**
 * The shared-layout identity of a graph node.
 *
 * A node reached by playing a suggestion borrows that suggestion's id, so the card and
 * the node are the same object as far as the layout engine is concerned. A node reached
 * by playing something unsuggested has no card to come from and uses its own id — it
 * slides in instead, which is the correct reading of what happened.
 */
export function nodeLayoutId(node: { id: string; fromSuggestionId?: string }): string {
  return `hc-node-${node.fromSuggestionId ?? node.id}`;
}

/** The layout identity of a branch card, matching `nodeLayoutId` for the node it becomes. */
export function branchLayoutId(suggestionId: string): string {
  return `hc-node-${suggestionId}`;
}

/**
 * The connector tint for a given adventurousness, 0..1.
 *
 * Calm sea-green through to ember. Interpolated in plain RGB, which is good enough over
 * this short a hue span and avoids shipping a colour library for six curves.
 */
export function adventurousnessTint(adventurousness: number): string {
  const t = Math.min(1, Math.max(0, adventurousness));
  const from = [127, 184, 168] as const; // --hc-calm
  const to = [224, 118, 92] as const; // --hc-ember
  const channel = (index: 0 | 1 | 2): number =>
    Math.round(from[index] + (to[index] - from[index]) * t);
  return `rgb(${channel(0)}, ${channel(1)}, ${channel(2)})`;
}

/**
 * Connector geometry. Exported as constants because the "playing something unsuggested
 * is not an error" guarantee is precisely the claim that a taken branch and an untaken
 * path are drawn with *identical* weight — so both must read from one number, and a test
 * asserts the rendered attributes match.
 */
export const CONNECTOR_STROKE_WIDTH = 2;
export const CONNECTOR_OPACITY = 0.55;
